import classNames from "classnames";
import React from "react";
import { c, t } from "ttag";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";

import { Trans } from "../../../common/Trans";
import { FormRadioSelect } from "../../../forms/FormRadioSelect";
import { FinancingPlan } from "../../../models/financing";
import { IMonths, isoAPR, isoMonths } from "../../../models/nominals";
import format from "../../../utils/format";
import { getDinero, getMonthlyPrice } from "../../../utils/money";
import { Dispatchers } from "../dispatchers";
import { IFinancingPayment, IReduxFormState } from "../reducers.interfaces";

interface IProps {
    methodKey: string;
    form: IReduxFormState;
    plans: FinancingPlan[];
    grandTotal: string;
    setPaymentMethodFields: Dispatchers["setPaymentMethodFields"];
    setPlanMonths: (months: IMonths) => void;
    disabled: boolean;
}

interface IState {}

// TODO(ssr, LEVEL 5 out of 10): Migrate this component from class-style to functional-style. Rated LOE as 5/10 due to financial calculations, plan selection logic, and componentDidMount lifecycle hook.
export class SelectPlanForm extends React.Component<IProps, IState> {
    componentDidMount() {
        this.props.setPlanMonths(this.props.plans[0].term_months);
    }

    private getMethodData(): IFinancingPayment {
        const data = this.props.form.payment_methods[this.props.methodKey];
        let wellsFargoData: IFinancingPayment | null = null;
        if (data.method_type === "financing") {
            wellsFargoData = data;
        }
        const baseFields: IFinancingPayment = {
            method_type: "financing",
            amount: "",
            pay_balance: false,
            new_financing_account: null,
            financing_account: "",
            financing_plan: null,
            has_agreed: false,
            has_esigned: false,
        };
        return {
            ...baseFields,
            ...wellsFargoData,
        };
    }

    private onChange(event: React.FormEvent<HTMLInputElement>) {
        const planID = parseInt(event.currentTarget.value, 10);
        const plan = this.props.plans.find((x) => {
            return x.id === planID;
        });
        if (!plan) {
            return;
        }
        const planMonths = plan.term_months;
        this.props.setPlanMonths(planMonths);
        this.props.setPaymentMethodFields(this.props.methodKey, {
            financing_plan: planID,
        });
    }

    private getTotal() {
        return (
            this.props.form.payment_methods[this.props.methodKey].amount ||
            "0.00"
        );
    }

    private getPlanDescription(plan: FinancingPlan) {
        if (
            plan.term_months <= isoMonths.wrap(0) ||
            parseFloat(isoAPR.unwrap(plan.apr)) > 0
        ) {
            return <span>{t`Regular Terms Apply`}</span>;
        }
        return (
            <span className="heavy">{c("SelectPlanForm")
                .t`${plan.term_months} months`}</span>
        );
    }

    private getPlanExtraDescription(plan: FinancingPlan) {
        return (
            <span className="tiny">
                {t`Minimum Financing Amount:`}{" "}
                {format.money(plan.product_price_threshold)}
            </span>
        );
    }

    private getEstimatedPayments(plan: FinancingPlan) {
        if (
            plan.term_months <= isoMonths.wrap(0) ||
            parseFloat(isoAPR.unwrap(plan.apr)) > 0
        ) {
            return (
                <span>
                    You can locate your rate on your monthly statement or by
                    calling Wells Fargo at: <br />
                    1-800-431-5921
                </span>
            );
        }
        const payment = getMonthlyPrice(
            getDinero(this.getTotal()),
            plan.term_months,
        );
        return (
            <span>
                <Trans
                    fmtString={interpolate(
                        t`%(payment)s/month<Superscript>2</Superscript> for %(length)s months (total payments of %(total)s)`,
                        {
                            payment: `${format.money(payment)}`,
                            length: `${plan.term_months}`,
                            total: `${format.money(this.getTotal())}`,
                        },
                    )}
                    data={{
                        Superscript: (content) => <sup key="1">{content}</sup>,
                    }}
                />
            </span>
        );
    }

    private buildTermHeader(plans: FinancingPlan[]) {
        if (plans.length === 1 && parseFloat(isoAPR.unwrap(plans[0].apr)) > 0) {
            return null;
        }

        const aprInfo = interpolate(
            t`Your order of %s qualifies for a special interest rate of 0% APR with equal monthly payments.`,
            [format.money(getDinero(this.getTotal()))],
        );

        return (
            <>
                <h3 className="checkout-step__copy--bold">
                    {t`Special Terms & APR Information`}
                </h3>
                <div className="form-options__financing-terms">{aprInfo}</div>
            </>
        );
    }

    render() {
        const formClass = classNames({
            "form-options-financing": true,
        });

        const plans = this.props.plans.map((plan) => {
            return {
                value: `${plan.id}`,
                label: this.getPlanDescription(plan),
                helpText: this.getEstimatedPayments(plan),
                extraText: !this.props.disabled
                    ? this.getPlanExtraDescription(plan)
                    : "",
                disabled:
                    this.props.disabled ||
                    parseFloat(plan.product_price_threshold) >
                        parseFloat(this.getTotal()),
            };
        });

        const termHeader = this.buildTermHeader(this.props.plans);

        const onChange = (event: React.FormEvent<HTMLInputElement>) => {
            this.onChange(event);
        };

        return (
            <div className={formClass}>
                {termHeader}
                <div className="form-options__financing-terms--choices">
                    <FormRadioSelect
                        name="financing_plan"
                        value={`${this.getMethodData().financing_plan}`}
                        onChange={onChange}
                        legend={t`Select term length:`}
                        choices={plans}
                    />
                </div>
            </div>
        );
    }
}
